import { useEffect, useState } from "react";
import { useRevalidator } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { PETS, type PetId, usePetSettings, writePetSettings } from "@/lib/pet";
import type { MeResponse } from "@/lib/types";
import { cn } from "@/lib/utils";
import { invalidateAuthCaches } from "@/src/lib/loaders";

interface ProfileTabProps {
  me: MeResponse;
}

export function ProfileTab({ me }: ProfileTabProps) {
  const [name, setName] = useState(me.user.name ?? "");
  const [saving, setSaving] = useState(false);
  const dirty = (name.trim() || null) !== (me.user.name ?? null);
  const revalidator = useRevalidator();

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await api<MeResponse>("/me", {
        method: "PATCH",
        body: JSON.stringify({ name: name.trim() }),
      });
      // Re-run dashboard-shell loader so the new name flows through the
      // auth context (and re-renders this tab + the header avatar).
      // The cached ``/me`` snapshot would still hold the old name, so
      // drop it before revalidating.
      invalidateAuthCaches();
      revalidator.revalidate();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update profile");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
          <CardDescription>Your display name and account email.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid max-w-md gap-4" onSubmit={onSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="profile-email">Email</Label>
              <Input id="profile-email" value={me.user.email} disabled readOnly />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="profile-name">Display name</Label>
              <Input
                id="profile-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Your name"
                maxLength={200}
              />
            </div>
            <div>
              <Button type="submit" disabled={!dirty || saving}>
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <ChatCompanionCard />
    </div>
  );
}

/**
 * Personal-preference card for the chat companion ("电子宠物"). All
 * state lives in `localStorage` — nothing hits the backend — so this
 * card mutates synchronously and broadcasts a same-tab event the
 * chat surface picks up via {@link usePetSettings}.
 *
 * Pet selection commits immediately on click. The nickname is a
 * controlled draft so the user can type freely and `Save` commits.
 */
function ChatCompanionCard() {
  const settings = usePetSettings();
  const [nameDraft, setNameDraft] = useState<string>(settings.name);
  const [nameDirty, setNameDirty] = useState<boolean>(false);

  // Adopt new values from `localStorage` (e.g. another tab) only
  // when the user isn't mid-edit on the nickname. Selecting a pet
  // tile bumps `settings.name` indirectly only when it's the same
  // value, so this stays stable in normal use.
  useEffect(() => {
    if (!nameDirty) setNameDraft(settings.name);
  }, [settings.name, nameDirty]);

  function selectPet(id: PetId) {
    // Preserve any in-flight nickname edit so changing the mascot
    // mid-rename doesn't silently discard what the user just typed.
    const name = nameDirty ? nameDraft.trim().slice(0, 24) : settings.name;
    writePetSettings({ id, name });
    if (nameDirty) {
      setNameDraft(name);
      setNameDirty(false);
    }
    toast.success(id === "off" ? "Companion turned off" : "Companion saved — say hi in chat!");
  }

  function onSaveName() {
    const trimmed = nameDraft.trim().slice(0, 24);
    writePetSettings({ id: settings.id, name: trimmed });
    setNameDraft(trimmed);
    setNameDirty(false);
    toast.success("Nickname saved");
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Chat companion</CardTitle>
        <CardDescription>
          Pick a tiny mascot ("电子宠物") to keep you company while the chat is empty or waiting for
          the agent's first reply. Lives in your browser only.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-5">
          <div>
            <Label className="mb-2 block text-[12px] uppercase tracking-wide text-muted-foreground">
              Choose a mascot
            </Label>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              <PetTile
                emoji="🚫"
                label="Off"
                active={settings.id === "off"}
                onClick={() => selectPet("off")}
                ariaLabel="Hide chat companion"
              />
              {PETS.map((pet) => (
                <PetTile
                  key={pet.id}
                  emoji={pet.emoji}
                  label={pet.label}
                  active={settings.id === pet.id}
                  onClick={() => selectPet(pet.id)}
                  ariaLabel={`Use ${pet.label} as chat companion`}
                />
              ))}
            </div>
          </div>

          <div className="grid max-w-md gap-1.5">
            <Label htmlFor="pet-name">Nickname (optional)</Label>
            <div className="flex gap-2">
              <Input
                id="pet-name"
                value={nameDraft}
                onChange={(e) => {
                  setNameDraft(e.target.value.slice(0, 24));
                  setNameDirty(true);
                }}
                placeholder="e.g. Mochi"
                maxLength={24}
                disabled={settings.id === "off"}
              />
              <Button
                type="button"
                variant="outline"
                onClick={onSaveName}
                disabled={!nameDirty || settings.id === "off"}
              >
                Save
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Up to 24 characters. Shown next to the mascot in the chat.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface PetTileProps {
  emoji: string;
  label: string;
  active: boolean;
  onClick: () => void;
  ariaLabel: string;
}

function PetTile({ emoji, label, active, onClick, ariaLabel }: PetTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={ariaLabel}
      className={cn(
        "group flex flex-col items-center gap-1 rounded-lg border bg-card px-2 py-3 text-[12px] transition",
        active
          ? "border-primary/60 bg-primary/5 ring-2 ring-primary/30"
          : "border-border/70 hover:border-border hover:bg-muted/40",
      )}
    >
      <span
        className={cn(
          "text-3xl leading-none transition group-hover:scale-110",
          active && "animate-pet-bob",
        )}
        aria-hidden="true"
      >
        {emoji}
      </span>
      <span
        className={cn("text-[11px] font-medium", active ? "text-primary" : "text-foreground/80")}
      >
        {label}
      </span>
    </button>
  );
}
